import { useSelector } from 'react-redux';
import { isManufacturerFamily, isRetailer, isCeilingPermissionType } from 'utils/companyTypes';

// Shared pages (SalesOrderList.jsx, CreateSalesOrder.jsx, CreatePurchaseOrder.jsx,
// etc.) call can('action', 'sales') / can('action', 'purchases') — the module
// names wholesaler/retailer/restaurant/distributor use. Manufacturer's own
// permission system uses different module names for the same concepts
// (sales_order, purchase). Rather than editing every shared page's can() call
// (error-prone, easy to miss one), this map lets can()/hasPermission() also
// try the manufacturer-equivalent module name when the company_type is
// 'manufacturer'. Wholesaler/retailer/restaurant/distributor are completely
// unaffected — the alias only applies when companyType === 'manufacturer'.
export const PACKING_ADVANCED_PERMISSION = 'packing_advanced.manage';

const MANUFACTURER_MODULE_ALIASES = {
  sales: 'sales_order',
  purchases: 'purchase'
};

export const usePermissions = () => {
  const permissions = useSelector((state) => state.auth.permissions);
  const user = useSelector((state) => state.auth.user);
  const companyType = useSelector((state) => state.auth.companyType);
  const isSuperAdmin = useSelector((state) => state.auth.isSuperAdmin);
  const isCompanyOwner = useSelector((state) => state.auth.isCompanyOwner);
  const parentCompanyId = useSelector((state) => state.auth.parentCompanyId);

  // Scope fetch fail/der se aaye to bhi retailer pehchana jaye: login user ka company_type fallback.
  const effectiveCompanyType = companyType || user?.user?.company_type || null;
  const isRetailerCompany = isRetailer(effectiveCompanyType);
  const userRole = user?.user?.role;

  const hasPermission = (permissionCode) => {
    // System Owner hamesha sab kuch
    if (isSuperAdmin || userRole === 'owner') {
      return true;
    }
    // Admin bypass: retailer me NAHI (admin bhi company ceiling ke andar rehta hai).
    // Baaqi sab company types (wholesaler, restaurant, distributor, manufacturer family) pehle jaisa.
    if (userRole === 'admin' && !isRetailerCompany) {
      return true;
    }
    return permissions.includes(permissionCode);
  };

  const hasAnyPermission = (permissionCodes = []) => {
    return permissionCodes.some((code) => hasPermission(code));
  };

  // Strict check — NO admin bypass. Backend ke checkManufacturerPermission /
  // assertPackingAdvancedAllowed jaisa: sirf System Owner bypass karta hai, baaqi
  // sab (admin bhi) effective permissions (company ceiling + role + user override)
  // se chalte hain. `permissions` manufacturer scope se effective codes hi hote hain.
  const hasEffectivePermission = (permissionCode) => {
    if (isSuperAdmin || userRole === 'owner') return true;
    return permissions.includes(permissionCode);
  };

  // Packing Raw Material / Ready Material (packing_advanced.manage).
  // Sirf manufacturer family par lagta hai; baaqi company types ke liye true
  // (unke liye ye feature gated hi nahi).
  const canUsePackingAdvanced = () => {
    if (!isManufacturerFamily(effectiveCompanyType)) return true;
    return hasEffectivePermission(PACKING_ADVANCED_PERMISSION);
  };

  const can = (action, module) => {
    if (hasPermission(`${module}.${action}`)) {
      return true;
    }
    // Manufacturer alias fallback (see MANUFACTURER_MODULE_ALIASES above).
    if (isManufacturerFamily(companyType)) {
      const aliasModule = MANUFACTURER_MODULE_ALIASES[module];
      if (aliasModule) {
        return hasPermission(`${aliasModule}.${action}`);
      }
    }
    return false;
  };

  // Task scope check — everything distributor-only must gate on this
  const isDistributor = companyType === 'distributor';

  // true only for a parent-level distributor company (no parent_company_id)
  const isParentDistributor = isDistributor && isSuperAdmin === false && isCompanyOwner && !parentCompanyId;

  // F3 — manufacturer equivalent. Fetch_My_Manufacturer_Scope() normalizes the
  // backend's `is_company_admin` onto isCompanyOwner, so this is safe to read
  // the same way isParentDistributor reads isCompanyOwner for distributor.
  const isManufacturer = isManufacturerFamily(companyType);
  const isManufacturerAdmin = isManufacturer && isSuperAdmin === false && isCompanyOwner;

  return {
    permissions,
    companyType,
    isRetailer: isRetailerCompany,
    isCeilingPermissionType: isCeilingPermissionType(effectiveCompanyType),
    isSuperAdmin,
    isCompanyOwner,
    parentCompanyId,
    isDistributor,
    isParentDistributor,
    isManufacturer,
    isManufacturerAdmin,
    hasPermission,
    hasAnyPermission,
    hasEffectivePermission,
    canUsePackingAdvanced,
    can
  };
};